<template>
  <n-config-provider :locale="naiveLocale" :theme-overrides="naiveThemeOverrides">
    <n-message-provider>
      <n-modal-provider>
        <div>
          <router-view />
          <Toast />
          <SessionExpiryAlert />
          <WebServiceErrorToast />
          <SearchModal
            ref="searchModal"
            :options="searchOptions"
            :quick-actions="quickActions"
            @select="handleSearchSelect"
          />
        </div>
      </n-modal-provider>
    </n-message-provider>
  </n-config-provider>
</template>

<script lang="ts">
import { defineComponent, onMounted, onBeforeUnmount, watch, computed } from "vue";
import { useAuthStore } from "@/stores/auth";
import sessionService from "@/services/session";
import { useRouter } from "vue-router";
import axios from "axios";
import Toast from "@/components/theme/ui/Toast.vue";
import SessionExpiryAlert from "@/components/atoms/SessionExpiryAlert.vue";
import WebServiceErrorToast from "@/components/atoms/WebServiceErrorToast.vue";
import SearchModal from "@/components/molecules/SearchModal.vue";
import { useSearchOptions } from "@/composables/useSearchOptions";
import { esAR, enUS, frFR, deDE, ptBR, NMessageProvider, NModalProvider } from "naive-ui";
import i18n from "@/i18n";

export default defineComponent({
  name: "App",
  components: {
    Toast,
    SessionExpiryAlert,
    WebServiceErrorToast,
    SearchModal,
    NMessageProvider,
    NModalProvider,
  },
  computed: {
    naiveLocale() {
      const localeMap = { es: esAR, en: enUS, fr: frFR, de: deDE, pt: ptBR };
      const base = localeMap[i18n.global.locale.value] || esAR;
      const overrides = {
        pt: {
          DataTable: { emptyMessage: "Sem dados" },
          Input: { placeholder: "Insire os dados, por favor" },
          InputNumber: { placeholder: "Insire os dados, por favor" },
          Select: { placeholder: "Selecione" },
        },
        es: {
          InputNumber: { placeholder: "Número" },
        },
      };
      const lang = i18n.global.locale.value;
      if (overrides[lang]) {
        return { ...base, ...overrides[lang] };
      }
      return base;
    },
    naiveThemeOverrides() {
      return {
        // Verde primario de Naive UI para las flechas de navegación del
        // calendario de todos los date pickers (por defecto son grises y
        // no se perciben como interactivas).
        DatePicker: {
          arrowColor: "#18a058",
        },
      };
    },
    searchOptions() {
      const { searchOptions } = useSearchOptions();
      return searchOptions;
    },
    quickActions() {
      const { quickActions } = useSearchOptions();
      return quickActions;
    },
  },
  methods: {
    handleSearchSelect(value, option) {
      // El componente ya maneja la navegación automáticamente
    },
  },
  setup() {
    const authStore = useAuthStore();
    const router = useRouter();

    const userActivityEvents = [
      "mousedown",
      "keydown",
      "mousemove",
      "touchstart",
      "click",
      "scroll",
    ];

    const resetTimer = (event: Event) => {
      if (authStore.token && router.currentRoute.value.path !== "/login") {
        sessionService.resetTimer();
      }
    };

    const checkInitialSessionState = async () => {
      const currentRoute = router.currentRoute.value.path;
      const isAuthenticated = !!authStore.token;

      if (isAuthenticated) {
        const token = authStore.token || authStore.tokenManager;
        if (token) {
          axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
        }
      }

      if (isAuthenticated && (await sessionService.isSessionExpired())) {
        try {
          await authStore.Logout();
          if (currentRoute !== "/login") await router.push("/login");
        } catch (error) {
          if (authStore.token) await authStore.Logout();
          if (currentRoute !== "/login") await router.push("/login");
        }
        return;
      }

      if (isAuthenticated && !sessionService.shouldTrackActivity) {
        sessionService.startSessionTimer();
      }
    };

    onMounted(async () => {
      await checkInitialSessionState();
      userActivityEvents.forEach((event) => {
        document.addEventListener(event, resetTimer, { passive: true });
      });
    });

    onBeforeUnmount(() => {
      userActivityEvents.forEach((event) => {
        document.removeEventListener(event, resetTimer);
      });
      sessionService.clearSessionTimer();
    });

    watch(
      () => authStore.token,
      (token) => {
        if (token) {
          sessionService.startSessionTimer();
        } else {
          sessionService.clearSessionTimer();
        }
      },
    );
  },
});
</script>
